/*
  Sunset Editorial — product-lightbox.js
  Visionneuse plein écran des visuels de la fiche produit : clic ou tap sur la galerie.

  - Ordinateur : clic = zoom à l'endroit cliqué, la vue suit ensuite la souris ; re-clic = vue entière.
    Molette = zoom progressif. Flèches gauche/droite, Échap.
  - Tactile : pincer pour zoomer, glisser pour se déplacer, double tap = zoom, balayer = image suivante.
  - Protection : visuel servi à une résolution plafonnée (Paramètres du thème > Protection des visuels),
    filigrane optionnel qui suit le zoom, image non sélectionnable ni enregistrable par clic droit.
*/
(() => {
  'use strict';

  if (customElements.get('product-lightbox')) return;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const MAX_ZOOM = 4;
  const CLICK_ZOOM = 2.5;
  const SWIPE = 60; // px pour changer d'image
  const DOUBLE_TAP = 300; // ms

  // Motif du filigrane : texte en diagonale, répété (SVG en image de fond, suit le zoom)
  const watermarkTile = (text) => {
    const safe = String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    const line = (x, y) =>
      `<text x="${x}" y="${y}" fill="rgba(255,255,255,0.3)" stroke="rgba(14,22,48,0.16)" stroke-width="0.8">${safe}</text>`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="440" height="260"><g transform="rotate(-24 220 130)" font-family="Futura, 'Century Gothic', Arial, sans-serif" font-size="22" font-weight="600" letter-spacing="4" text-anchor="middle">${line(220, 122)}${line(0, 252)}${line(440, 252)}</g></svg>`;
    return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
  };

  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('[data-stage]');
      this.frame = this.querySelector('[data-frame]');
      this.img = this.querySelector('[data-img]');
      this.count = this.querySelector('[data-count]');
      this.hint = this.querySelector('[data-hint]');
      this.mark = this.querySelector('[data-watermark]');
      this.scope = this.closest('product-info') || document;
      this.gallery = this.scope.querySelector('media-gallery');
      if (!this.dialog || !this.stage || !this.gallery) return;
      this.ready = true;
      this.protect = this.dataset.protect === 'true';
      this.touchQuery = window.matchMedia('(hover: none) and (pointer: coarse)');

      this.collect();
      if (!this.items.length) return;
      if (this.mark && this.dataset.watermark) this.mark.style.backgroundImage = watermarkTile(this.dataset.watermark);
      this.toggleAttribute('data-single', this.items.length < 2);

      this.onGalleryClick = (event) => {
        const slide = event.target.closest('[data-zoom-src]');
        if (!slide || event.defaultPrevented) return;
        event.preventDefault();
        this.trigger = event.target.closest('button') || slide;
        this.open(this.items.findIndex((item) => item.slide === slide));
      };
      this.gallery.addEventListener('click', this.onGalleryClick);

      this.querySelector('[data-close]').addEventListener('click', () => this.close());
      this.querySelector('[data-prev]').addEventListener('click', () => this.show(this.index - 1));
      this.querySelector('[data-next]').addEventListener('click', () => this.show(this.index + 1));
      // Fermeture gérée par le thème (bouton, Échap) ; l'évènement « close » reste un filet de sécurité
      this.dialog.addEventListener('close', () => this.onClosed());
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
        else if (event.key === 'ArrowRight') this.show(this.index + 1);
        else if (event.key === '+' || event.key === '=') this.zoomTo(this.scale * 1.5, 0, 0, true);
        else if (event.key === '-') this.zoomTo(this.scale / 1.5, 0, 0, true);
      });
      if (this.protect) this.dialog.addEventListener('contextmenu', (event) => event.preventDefault());

      this.pointers = new Map();
      this.stage.addEventListener('pointerdown', (event) => this.onDown(event));
      this.stage.addEventListener('pointermove', (event) => this.onMove(event));
      this.stage.addEventListener('pointerup', (event) => this.onUp(event));
      this.stage.addEventListener('pointercancel', (event) => this.pointers.delete(event.pointerId));
      this.stage.addEventListener(
        'wheel',
        (event) => {
          event.preventDefault();
          const p = this.local(event);
          this.zoomTo(this.scale * Math.exp(-event.deltaY * 0.0015), p.x, p.y, false);
        },
        { passive: false }
      );
      this.onResize = () => {
        if (this.dialog.open) this.fit();
      };
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      this.gallery?.removeEventListener('click', this.onGalleryClick);
      window.removeEventListener('resize', this.onResize);
      document.documentElement.classList.remove('lightbox-open');
      this.ready = false;
    }

    // Visuels zoomables de la galerie, dans l'ordre d'affichage, avec un bouton « Agrandir » chacun
    collect() {
      this.items = Array.from(this.gallery.querySelectorAll('[data-zoom-src]')).map((slide) => {
        if (!slide.querySelector('.gallery__zoom')) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'gallery__zoom';
          button.setAttribute('aria-label', this.dataset.openLabel || 'Agrandir');
          button.innerHTML =
            '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5 21 21M10.5 7.5v6M7.5 10.5h6"/></svg>';
          slide.appendChild(button);
        }
        return {
          slide,
          src: slide.dataset.zoomSrc,
          width: Number(slide.dataset.zoomW) || 1000,
          height: Number(slide.dataset.zoomH) || 1000,
          alt: slide.dataset.zoomAlt || '',
          preview: () => slide.querySelector('img')?.currentSrc,
        };
      });
    }

    open(index) {
      if (index < 0) return;
      this.isOpen = true;
      document.documentElement.classList.add('lightbox-open');
      this.dialog.showModal();
      this.show(index);
      this.querySelector('[data-close]').focus({ preventScroll: true });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
      this.onClosed();
    }

    onClosed() {
      if (!this.isOpen) return;
      this.isOpen = false;
      document.documentElement.classList.remove('lightbox-open');
      // La galerie (carrousel mobile) se cale sur la dernière image vue
      this.gallery.goToIndex?.(Array.from(this.gallery.querySelectorAll('[data-media-id]')).indexOf(this.items[this.index]?.slide));
      this.trigger?.focus?.({ preventScroll: true });
    }

    show(index) {
      const n = this.items.length;
      this.index = ((index % n) + n) % n;
      const item = this.items[this.index];
      // Affichage immédiat avec l'image déjà chargée par la galerie, puis la version nette
      const preview = item.preview();
      this.img.src = preview || item.src;
      this.img.alt = item.alt;
      if (preview && preview !== item.src) {
        const sharp = new Image();
        sharp.onload = () => {
          if (this.items[this.index] === item) this.img.src = item.src;
        };
        sharp.src = item.src;
      }
      // Images voisines préchargées
      [this.index - 1, this.index + 1].forEach((i) => {
        const neighbour = this.items[((i % n) + n) % n];
        if (neighbour && neighbour !== item) new Image().src = neighbour.src;
      });
      if (this.count) this.count.textContent = `${this.index + 1} / ${n}`;
      this.fit();
    }

    // Image entière, centrée, au plus grand possible dans la scène
    fit() {
      const item = this.items[this.index];
      const sw = this.stage.clientWidth;
      const sh = this.stage.clientHeight;
      const ratio = item.width / item.height;
      this.fw = Math.min(sw, sh * ratio);
      this.fh = this.fw / ratio;
      this.frame.style.width = `${this.fw}px`;
      this.frame.style.height = `${this.fh}px`;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.apply(false);
    }

    // Zoom plafonné à la résolution réelle du visuel servi (pas d'agrandissement flou)
    maxZoom() {
      const item = this.items[this.index];
      return clamp(item.width / this.fw, 1.5, MAX_ZOOM);
    }

    local(event) {
      const r = this.stage.getBoundingClientRect();
      return { x: event.clientX - r.left - r.width / 2, y: event.clientY - r.top - r.height / 2 };
    }

    // Zoom autour d'un point (coordonnées depuis le centre de la scène), le point reste sous le doigt
    zoomTo(scale, x, y, animate) {
      const next = clamp(scale, 1, this.maxZoom());
      const k = next / this.scale;
      this.tx = x - (x - this.tx) * k;
      this.ty = y - (y - this.ty) * k;
      this.scale = next;
      this.apply(animate);
    }

    apply(animate) {
      const maxX = Math.max(0, (this.fw * this.scale - this.stage.clientWidth) / 2);
      const maxY = Math.max(0, (this.fh * this.scale - this.stage.clientHeight) / 2);
      if (this.scale <= 1.001) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
      }
      this.tx = clamp(this.tx, -maxX, maxX);
      this.ty = clamp(this.ty, -maxY, maxY);
      // 'zoom' : changement de niveau ; 'follow' : suivi doux de la souris ; false : immédiat (doigts)
      this.frame.style.transition =
        animate === 'follow' ? 'transform 0.16s ease-out' : animate ? 'transform 0.3s cubic-bezier(0.2, 0.7, 0.2, 1)' : 'none';
      this.frame.style.transform = `translate3d(${this.tx.toFixed(1)}px, ${this.ty.toFixed(1)}px, 0) scale(${this.scale.toFixed(4)})`;
      this.stage.classList.toggle('is-zoomed', this.scale > 1);
      if (this.scale > 1) this.dialog.classList.add('has-zoomed');
    }

    // Souris : la vue zoomée suit le pointeur sur toute la scène
    follow(event, animate = 'follow') {
      const r = this.stage.getBoundingClientRect();
      const maxX = Math.max(0, (this.fw * this.scale - r.width) / 2);
      const maxY = Math.max(0, (this.fh * this.scale - r.height) / 2);
      this.tx = -(((event.clientX - r.left) / r.width) * 2 - 1) * maxX;
      this.ty = -(((event.clientY - r.top) / r.height) * 2 - 1) * maxY;
      this.apply(animate);
    }

    onDown(event) {
      try {
        this.stage.setPointerCapture(event.pointerId);
      } catch (error) {
        // pointeur déjà relâché : sans capture, le geste reste suivi par la scène
      }
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY });
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.scale };
      }
      this.panStart = { x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty };
    }

    onMove(event) {
      if (event.pointerType === 'mouse' && this.pointers.size === 0) {
        if (this.scale > 1) this.follow(event);
        return;
      }
      const p = this.pointers.get(event.pointerId);
      if (!p) return;
      p.x = event.clientX;
      p.y = event.clientY;
      if (this.pointers.size === 2 && this.pinch) {
        const [a, b] = [...this.pointers.values()];
        const r = this.stage.getBoundingClientRect();
        const mid = { x: (a.x + b.x) / 2 - r.left - r.width / 2, y: (a.y + b.y) / 2 - r.top - r.height / 2 };
        this.zoomTo(this.pinch.scale * (Math.hypot(a.x - b.x, a.y - b.y) / this.pinch.dist), mid.x, mid.y, false);
      } else if (this.pointers.size === 1 && this.scale > 1 && event.pointerType !== 'mouse') {
        this.tx = this.panStart.tx + (event.clientX - this.panStart.x);
        this.ty = this.panStart.ty + (event.clientY - this.panStart.y);
        this.apply(false);
      }
    }

    onUp(event) {
      const p = this.pointers.get(event.pointerId);
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinch = null;
      if (!p) return;
      const dx = event.clientX - p.startX;
      const dy = event.clientY - p.startY;
      const moved = Math.hypot(dx, dy) > 8;
      const point = this.local(event);

      if (event.pointerType === 'mouse') {
        if (moved) return;
        if (this.scale > 1) this.zoomTo(1, 0, 0, true);
        else {
          // Zoom vers l'endroit cliqué, puis la vue suit la souris
          this.scale = Math.min(CLICK_ZOOM, this.maxZoom());
          this.follow(event, true);
        }
        return;
      }

      // Tactile : balayer pour changer d'image (vue entière), double tap pour zoomer
      if (this.scale === 1 && Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.show(this.index + (dx < 0 ? 1 : -1));
        return;
      }
      if (moved || this.pointers.size) return;
      const now = performance.now();
      if (this.lastTap && now - this.lastTap.time < DOUBLE_TAP && Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 30) {
        this.lastTap = null;
        if (this.scale > 1) this.zoomTo(1, 0, 0, true);
        else this.zoomTo(Math.min(CLICK_ZOOM, this.maxZoom()), point.x, point.y, true);
      } else {
        this.lastTap = { time: now, x: event.clientX, y: event.clientY };
      }
    }
  }

  customElements.define('product-lightbox', ProductLightbox);
})();
